/* eslint-disable import/first */
import { log } from '../log';
import { getSimpleRemoteConfig } from '../remote-config-manager';
import { enums } from '../';
import { getSessionInfo, postLogout } from '../web-apis-client';

declare global {
  interface Window {
    Cobrowse: any;
    dataLayer: any;
  }
}
declare global {
  namespace NodeJS {
    interface Global {
      document: Document;
      window: Window;
      navigator: Navigator;
    }
  }
}

/**
 * Clears stored credentials
 */
export const clearAWSCredentials = () => {
  sessionStorage.removeItem(enums.CacheKeys.sessionCredentials);
  sessionStorage.removeItem(enums.CacheKeys.userInfo);
};

let awaitingLogout = false;

const LOGOUT_TIMEOUT = 10000;

type LogoutOptions = {
  strict?: boolean;
  timeoutMs?: number;
};

const logoutPostRequest = async (timeoutMs?: number) => {
  if (!timeoutMs || timeoutMs <= 0) {
    await postLogout();
    return;
  }

  await Promise.race([
    postLogout(),
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`logout timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    }),
  ]);
};

const clearSessionAndRedirect = async (redirectUrl: string) => {
  const browserWindow = globalThis as unknown as Window;

  if (browserWindow.Cobrowse?.modal) {
    browserWindow.Cobrowse.modal.endSession(); //Close a CoBrowse session if one is open
  }

  const entryPoint = sessionStorage.getItem(enums.CacheKeys.entryPoint);
  localStorage.clear();
  sessionStorage.clear();

  // restore entry point for later use
  if (entryPoint) {
    sessionStorage.setItem(enums.CacheKeys.entryPoint, entryPoint);
  }

  browserWindow.location.assign(redirectUrl);
};

export const logout = async (options: LogoutOptions = {}) => {
  const { strict = false, timeoutMs = LOGOUT_TIMEOUT } = options;
  const remoteConfig = await getSimpleRemoteConfig();
  sessionStorage.removeItem(enums.CacheKeys.ssoCookieKey);
  sessionStorage.removeItem(enums.CacheKeys.ssoCookieValue);

  if (awaitingLogout) {
    return;
  }

  awaitingLogout = true;

  try {
    await logoutPostRequest(timeoutMs);
  } catch (e) {
    log.error(e);

    if (strict) {
      throw e;
    }
  } finally {
    awaitingLogout = false;
  }

  const redirectUrl = remoteConfig['login_redirect_url'];
  await clearSessionAndRedirect(redirectUrl);
};

/**
 * Check if the user appears to be logged in
 * @returns {boolean}
 */
export const isAuthenticated = async () => {
  try {
    // for example, we can show the user the time their session will expire if we want
    const userInfo = await getSessionInfo();
    sessionStorage.setItem(
      enums.CacheKeys.userInfo,
      JSON.stringify({
        email: userInfo.username,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        eid: userInfo.eid,
      }),
    );
  } catch (e) {
    console.error(e);
    await logout();
    return false;
  }

  return true;
};
